<div
  class="conversation-container"
  role="main"
  aria-label="Conversation interface"
>
  <div
    *ngIf="!isConnectionHealthy()"
    class="connection-status-bar"
    [class.connecting]="connectionState === 'connecting'"
    [class.disconnected]="connectionState === 'disconnected'"
    role="alert"
    aria-live="polite"
  >
    <div class="status-content">
      <span class="status-icon" aria-hidden="true">
        <span *ngIf="connectionState === 'connecting'" class="spinner"></span>
        <span *ngIf="connectionState === 'disconnected'">⚠️</span>
      </span>
      <span class="status-text">{{ getConnectionStatusText() }}</span>
      <button
        *ngIf="connectionState === 'disconnected'"
        class="retry-btn"
        (click)="retryConnection()"
        type="button"
        aria-label="Retry connection"
      >
        Retry
      </button>
    </div>
  </div>

  <div
    *ngIf="loading"
    class="loading-container"
    role="status"
    aria-live="polite"
  >
    <div class="loading-spinner" aria-hidden="true"></div>
    <p>Loading conversation...</p>
  </div>

  <div
    *ngIf="error && !loading"
    class="error-container"
    role="alert"
    aria-live="assertive"
  >
    <div class="error-card">
      <div class="error-icon" aria-hidden="true">⚠️</div>
      <h3>Something went wrong</h3>
      <p>{{ error }}</p>
      <div class="error-actions">
        <button
          class="btn-retry"
          (click)="retry()"
          type="button"
          aria-label="Retry loading conversation"
        >
          <span aria-hidden="true">🔄</span>
          Try Again
        </button>
        <button
          class="btn-back"
          (click)="goBack()"
          type="button"
          aria-label="Go back to previous page"
        >
          <span aria-hidden="true">←</span>
          Go Back
        </button>
      </div>
    </div>
  </div>

  <div *ngIf="!loading && !error && conversation" class="conversation-content">
    <header class="conversation-header" role="banner">
      <div class="header-left">
        <button
          class="btn-back-header"
          (click)="goBack()"
          type="button"
          aria-label="Go back to conversations list"
          title="Back to conversations"
        >
          <span class="back-icon" aria-hidden="true">←</span>
        </button>

        <div class="conversation-info">
          <div class="user-avatar-container">
            <div
              class="user-avatar"
              [attr.aria-label]="'Avatar for ' + getOtherUserName()"
              role="img"
            >
              {{ getOtherUserName().charAt(0).toUpperCase() }}
            </div>
            <div
              *ngIf="isOtherUserOnline()"
              class="online-indicator"
              title="Online"
              aria-label="User is online"
            ></div>
          </div>

          <div class="user-details">
            <h1 class="user-name" [title]="getOtherUserName()">
              {{ getOtherUserName() }}
              <span
                *ngIf="isOtherUserOnline()"
                class="online-text"
                aria-label="Online status"
              >
                • Online
              </span>
            </h1>

            <div class="listing-info" *ngIf="conversation.listing">
              <span
                class="listing-title clickable"
                [title]="getListingTitle()"
                aria-label="Listing title"
                (click)="goToListing(conversation.listing)"
                (keydown.enter)="goToListing(conversation.listing)"
                (keydown.space)="goToListing(conversation.listing)"
                role="button"
                tabindex="0"
                [attr.aria-label]="'View listing: ' + getListingTitle()"
              >
                {{ getListingTitle() }}
              </span>

              <span
                class="listing-price"
                *ngIf="conversation.listing.price"
                [attr.aria-label]="
                  'Price: ' + formatPrice(conversation.listing.price)
                "
              >
                {{ formatPrice(conversation.listing.price) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <section
      class="messages-area"
      #messagesContainer
      role="log"
      aria-live="polite"
      aria-label="Conversation messages"
      [attr.aria-busy]="loadingMessages"
    >
      <div class="messages-list">
        <ng-container
          *ngFor="
            let message of messages;
            let i = index;
            trackBy: trackByMessage
          "
        >
          <div
            *ngIf="shouldShowDateSeparator(i)"
            class="date-separator"
            role="separator"
            [attr.aria-label]="'Messages from ' + getMessageDate(message)"
          >
            <span class="date-text">{{ getMessageDate(message) }}</span>
          </div>

          <article
            class="message-wrapper"
            [class.my-message]="isMyMessage(message)"
            [class.pending]="message.isPending"
            [class.failed]="message.sendFailed"
            [attr.aria-label]="
              (isMyMessage(message)
                ? 'Your message'
                : getOtherUserName() + '\'s message') +
              ' sent at ' +
              getMessageTime(message)
            "
          >
            <div
              class="message-bubble"
              [class.sent]="isMyMessage(message)"
              [class.received]="!isMyMessage(message)"
              [class.pending]="message.isPending"
              [class.failed]="message.sendFailed"
              role="article"
            >
              <div class="message-content">
                <p
                  class="message-text"
                  [attr.aria-label]="message.message_text"
                >
                  {{ message.message_text }}
                </p>

                <footer class="message-meta" aria-label="Message details">
                  <time
                    class="message-time"
                    [attr.datetime]="message.created_at"
                    [attr.aria-label]="'Sent at ' + getMessageTime(message)"
                  >
                    {{ getMessageTime(message) }}
                  </time>

                  <div *ngIf="isMyMessage(message)" class="message-status">
                    <span
                      *ngIf="getMessageStatus(message) === 'sending'"
                      class="status-sending"
                      aria-label="Message sending"
                      title="Sending..."
                    >
                      ○
                    </span>
                    <span
                      *ngIf="getMessageStatus(message) === 'read'"
                      class="status-read"
                      aria-label="Message read"
                      title="Read"
                    >
                      ✓✓
                    </span>
                    <span
                      *ngIf="getMessageStatus(message) === 'delivered'"
                      class="status-delivered"
                      aria-label="Message delivered"
                      title="Delivered"
                    >
                      ✓
                    </span>
                    <span
                      *ngIf="getMessageStatus(message) === 'failed'"
                      class="status-failed"
                      aria-label="Message failed"
                      title="Failed to send"
                    >
                      ⚠
                    </span>
                  </div>
                </footer>

                <div *ngIf="message.sendFailed" class="message-failed-actions">
                  <button
                    class="retry-message-btn"
                    (click)="retryFailedMessage(message)"
                    type="button"
                    title="Retry sending"
                  >
                    Retry
                  </button>
                  <button
                    class="remove-message-btn"
                    (click)="removeFailedMessage(message)"
                    type="button"
                    title="Remove message"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </article>
        </ng-container>

        <div
          *ngIf="messages.length === 0 && !loadingMessages"
          class="no-messages"
          role="status"
          aria-live="polite"
        >
          <div class="no-messages-icon" aria-hidden="true">💬</div>
          <p>No messages yet. Start the conversation!</p>
        </div>

        <div
          *ngIf="loadingMessages"
          class="loading-messages"
          role="status"
          aria-live="polite"
        >
          <div class="loading-spinner-small" aria-hidden="true"></div>
          <span>Loading messages...</span>
        </div>
      </div>
    </section>

    <footer class="message-input-area" role="contentinfo">
      <form
        (ngSubmit)="sendMessage()"
        class="input-container"
        role="form"
        aria-label="Send message form"
      >
        <label for="messageInput" class="sr-only">
          Type your message to {{ getOtherUserName() }}
        </label>

        <textarea
          id="messageInput"
          #messageInput
          [(ngModel)]="newMessage"
          (keydown)="onKeyDown($event)"
          (input)="onInputChange()"
          [placeholder]="getPlaceholderText()"
          [disabled]="sendingMessage || !isConnectionHealthy()"
          class="message-input"
          rows="1"
          maxlength="1000"
          name="message"
          autocomplete="off"
          aria-describedby="characterCount inputHint"
          [attr.aria-invalid]="newMessage.length > 1000"
        ></textarea>

        <button
          type="submit"
          class="send-button"
          [disabled]="!canSendMessage()"
          [class.sending]="sendingMessage"
          [attr.aria-label]="
            sendingMessage ? 'Sending message' : 'Send message'
          "
          [title]="sendingMessage ? 'Sending...' : 'Send message (Enter)'"
        >
          <span *ngIf="!sendingMessage" class="send-icon" aria-hidden="true"
            >➤</span
          >
          <span
            *ngIf="sendingMessage"
            class="sending-spinner"
            aria-hidden="true"
          ></span>
        </button>
      </form>

      <div class="input-footer">
        <span
          id="characterCount"
          class="character-count"
          [attr.aria-label]="newMessage.length + ' of 1000 characters used'"
          [style.color]="newMessage.length > 900 ? '#ef4444' : null"
        >
          {{ newMessage.length }}/1000
        </span>

        <span id="inputHint" class="input-hint" aria-live="polite">
          Press Enter to send, Shift+Enter for new line
        </span>
      </div>
    </footer>
  </div>
</div>
